/**
 * Dashboard — realtime, health-first control center.
 *
 * One aggregate (useDashboardOverview) polled every ~8s backs a live KPI row, a live
 * throughput/clients activity chart (UniFi-style), a top-APs strip, and the by-type
 * breakdown. The "needs attention" worklist (its own poll) stays the actionable heart.
 * A "Live · updated Ns ago" pulse makes the freshness legible. Throughput comes from the
 * DeviceTelemetry byte-counter deltas the controller now aggregates server-side.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useHealthWorklist, useDashboardOverview } from "@/lib/api/hooks";
import { useSession } from "@/stores/session";
import { orgLabel } from "@/lib/quarantine";
import { PageHeader, ErrorBanner } from "@/components/ui/Page";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import type { HealthAlert, DashboardOverview } from "@/types/domain";
import { relTime, parseApiTime } from "@/lib/format";
import { ApiError } from "@/lib/api/client";
import { alertGuide, alertsHref, offlineDevicesHref, worklistView } from "@/lib/alertGuide";
import { tileScopeLabel } from "@/lib/tileScope";
import { ScopeLabel, useScopeNames } from "@/components/data/ScopeLabel";

/** Rows the "Needs attention" block shows before handing off to the full Alerts list. */
const WORKLIST_PREVIEW = 5;

/** Friendly look-back label: 45 min / 6 h / 1 d / 7 d. */
function fmtWindow(secs: number): string {
  if (secs < 3600) return `${Math.round(secs / 60)} min`;
  if (secs < 86400) return `${Math.round(secs / 3600)} h`;
  return `${Math.round(secs / 86400)} d`;
}

/** Human bit/s: 24.0 Kbps / 3.1 Mbps / 1.2 Gbps. */
function fmtBps(n: number): string {
  if (!n || n < 1000) return `${Math.round(n || 0)} bps`;
  if (n < 1e6) return `${(n / 1e3).toFixed(1)} Kbps`;
  if (n < 1e9) return `${(n / 1e6).toFixed(1)} Mbps`;
  return `${(n / 1e9).toFixed(2)} Gbps`;
}

const RANGES: { label: string; secs: number }[] = [
  { label: "1h", secs: 3600 },
  { label: "6h", secs: 21600 },
  { label: "1D", secs: 86400 },
  { label: "1W", secs: 604800 },
];

export default function Dashboard() {
  const [rangeSec, setRangeSec] = useState(3600);
  const overview = useDashboardOverview(rangeSec);
  const worklist = useHealthWorklist();
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
// Quarantine holding area is labelled, never shown by its stored name (2026-10-19).
  const orgName = useSession((s) => orgLabel(s.currentOrg()));

  const ov = overview.data;
  const items = worklist.data?.items ?? [];
  // OP#1854: what the Needs-attention block may say. "All clear" only when the list fetch
  // SUCCEEDED empty and the tile count for the same scope is zero (lib/alertGuide).
  const view = worklistView({
    isError: worklist.isError,
    isLoading: worklist.isLoading,
    itemCount: items.length,
    tileTotal: ov?.alerts.total,
  });
  const alertsTotal = worklist.data?.total ?? ov?.alerts.total ?? items.length;
  // Every count links to the list it counts (OP#1854).
  const allAlerts = orgId ? alertsHref(orgId, siteId) : "#";
  const criticalAlerts = orgId ? alertsHref(orgId, siteId, { severity: "critical" }) : "#";

  // OP#1787: every KPI tile states the scope it counts. The scope is the one the SERVER reports
  // it counted (overview.scope, api/v2/dashboard.tile_scope) -- not re-derived from the
  // selector -- so the label can never describe a different set than the number beside it.
  // The worklist below is fetched with the same org/site selection, so tiles and list agree.
  const scopeNames = useScopeNames();
  const scopeLabel = tileScopeLabel(ov?.scope, scopeNames);
  const tileScope = <ScopeLabel label={scopeLabel} className="mt-2" />;

  const scoped = (seg: string) => (siteId ? `/o/${orgId}/s/${siteId}/${seg}` : `/o/${orgId}/${seg}`);
  const deviceHref = (serial: string) =>
    siteId ? `/o/${orgId}/s/${siteId}/devices/${serial}` : `/o/${orgId}/devices/${serial}`;

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader title="Health" subtitle={orgName ? `${orgName} — control center` : undefined} />
        <LivePulse updatedAt={overview.dataUpdatedAt} fetching={overview.isFetching} />
      </div>
      {overview.error && <ErrorBanner error={overview.error} />}

      {/* ---- KPI hero row ---- */}
      {/* md-stagger: the four tiles rise in left-to-right on load, so the control center reads as
          coming online rather than flashing in. */}
      <section aria-label="Key metrics" className="md-stagger mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {/* Fleet health opens the alerts behind the score (the "less open issues" part). */}
        <Link
          to={allAlerts}
          className="md-card md-lift flex items-center gap-4 p-4 hover:bg-surface-2"
          aria-label={`Fleet health ${ov?.health.score ?? "unavailable"}: open the alerts list`}
        >
          <HealthGauge value={ov?.health.score ?? null} tone={ov?.health.tone ?? "unknown"} />
          <div>
            <div className="text-xs text-ink-muted">Fleet health</div>
            <div className="text-[0.6875rem] text-ink-subtle">online, less open issues</div>
            {ov && ov.alerts.total > 0 && (
              <div className="mt-1 text-xs text-ink-muted">
                {ov.alerts.total} open {ov.alerts.total === 1 ? "alert" : "alerts"} →
              </div>
            )}
            {tileScope}
          </div>
        </Link>

        {/* With devices offline the tile opens the Devices list FILTERED to them (OP#1854). */}
        <Link
          to={ov && ov.devices.offline > 0 && orgId ? offlineDevicesHref(orgId, siteId) : scoped("devices")}
          className="md-card md-lift block p-4 hover:bg-surface-2"
        >
          <div className="text-xs text-ink-muted">Active devices</div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="text-3xl font-semibold tabular-nums text-ink">{ov?.devices.online ?? "—"}</span>
            <span className="text-sm text-ink-subtle">/ {ov?.devices.total ?? "—"}</span>
          </div>
          <div className="mt-1 text-xs">
            {ov && ov.devices.offline > 0 ? (
              <span className="text-warn">{ov.devices.offline} offline →</span>
            ) : (
              <span className="text-healthy">all online</span>
            )}
          </div>
          {tileScope}
        </Link>

        <Link to={scoped("clients")} className="md-card md-lift block p-4 hover:bg-surface-2">
          <div className="text-xs text-ink-muted">Live clients</div>
          <div className="mt-1 text-3xl font-semibold tabular-nums text-ink">{ov?.clients.count ?? "—"}</div>
          <div className="mt-1 text-xs text-ink-subtle">connected now →</div>
          {tileScope}
        </Link>

        {/* Needs attention: the number opens every open alert; "N critical" opens just the
            critical ones. Two sibling links (never nested) so each count has its own target. */}
        <div className="md-card md-lift p-4 hover:bg-surface-2" data-testid="tile-needs-attention">
          <Link
            to={allAlerts}
            className="block"
            aria-label={`${ov?.alerts.total ?? items.length} open alerts: open the list`}
          >
            <div className="text-xs text-ink-muted">Needs attention</div>
            <div
              className={`mt-1 text-3xl font-semibold tabular-nums ${
                ov?.alerts.critical ? "text-critical" : ov?.alerts.warn ? "text-warn" : "text-ink"
              }`}
            >
              {ov?.alerts.total ?? items.length}
            </div>
          </Link>
          <div className="mt-1 text-xs">
            {ov && ov.alerts.critical > 0 ? (
              <Link to={criticalAlerts} className="text-critical underline-offset-2 hover:underline">
                {ov.alerts.critical} critical →
              </Link>
            ) : (
              <span className="text-ink-subtle">none critical</span>
            )}
          </div>
          {tileScope}
        </div>
      </section>

      {/* ---- Activity chart (live + historical ranges) ---- */}
      <section aria-label="Network activity" className="mb-6">
        <div className="mb-2 flex items-center justify-end gap-1">
          {RANGES.map((r) => (
            <button
              key={r.secs}
              type="button"
              onClick={() => setRangeSec(r.secs)}
              aria-pressed={rangeSec === r.secs}
              className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                rangeSec === r.secs ? "bg-brand text-ink-invert" : "border border-line text-ink-muted hover:border-brand hover:text-ink"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <ActivityChart overview={ov} loading={overview.isLoading} />
      </section>

      {/* ---- Top APs (by live client load) ---- */}
      {ov && ov.top_aps.length > 0 && (
        <section aria-label="Top access points" className="mb-6">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">Top APs by clients</h2>
          <div className="md-stagger grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {ov.top_aps.map((ap) => {
              const max = ov.top_aps[0]?.clients || 1;
              return (
                <Link key={ap.serial} to={deviceHref(ap.serial)} className="md-card md-lift flex items-center gap-3 p-3 hover:bg-surface-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-ink">{ap.name}</div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-2">
                      {/* The load bar eases out to its width on mount (transition-[width]); it is a
                          data meter, so it keeps the brand fill the surrounding code already uses. */}
                      <div
                        className="h-full rounded-full bg-brand transition-[width] duration-500 ease-out"
                        style={{ width: `${Math.max(6, (ap.clients / max) * 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-lg font-semibold tabular-nums text-ink">{ap.clients}</div>
                    <div className="text-[0.625rem] uppercase tracking-wide text-ink-subtle">clients</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ---- The worklist LEADS the detail ---- */}
      <section aria-label="Needs attention" className="mb-6">
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Needs attention</h2>
          {view === "list" && (
            <Link to={allAlerts} className="text-xs text-brand underline-offset-2 hover:underline">
              View all {alertsTotal} {alertsTotal === 1 ? "alert" : "alerts"} →
            </Link>
          )}
        </div>
        {view === "error" ? (
          // A failed fetch is NOT "nothing needs attention" (OP#1854 / F3).
          <div
            role="alert"
            className="md-card border-critical/40 bg-critical-soft p-4 text-sm"
            data-testid="worklist-error"
          >
            <div className="font-medium text-critical">
              {ov && ov.alerts.total > 0
                ? `${ov.alerts.total} ${ov.alerts.total === 1 ? "alert" : "alerts"} could not be listed`
                : "Alerts could not be loaded"}
            </div>
            <p className="mt-1 text-ink-muted">
              {worklist.error instanceof ApiError && worklist.error.isTransport
                ? "Meridian could not reach the controller."
                : `The controller answered with an error${
                    worklist.error instanceof Error ? `: ${worklist.error.message}` : "."
                  }`}
            </p>
            <div className="mt-3">
              <Button variant="secondary" onClick={() => void worklist.refetch()} disabled={worklist.isFetching}>
                {worklist.isFetching ? "Retrying…" : "Retry"}
              </Button>
            </div>
          </div>
        ) : view === "loading" ? (
          <div className="md-card p-4 text-sm text-ink-muted" aria-busy>
            Loading alerts…
          </div>
        ) : view === "unlisted" ? (
          // Counted but not listed: say so and hand off, never "All clear".
          <div role="status" className="md-card p-4 text-sm" data-testid="worklist-unlisted">
            <div className="font-medium text-ink">
              {ov?.alerts.total} open {ov?.alerts.total === 1 ? "alert is" : "alerts are"} counted here but none
              could be listed
            </div>
            <Link to={allAlerts} className="mt-2 inline-block text-brand underline-offset-2 hover:underline">
              Open the alerts list →
            </Link>
          </div>
        ) : view === "clear" ? (
          <div className="md-card flex items-center gap-3 p-4">
            {/* "All clear" is the one healthy-ramp moment that earns a glyph: a check in a soft
                healthy disc. The ramp colour is legitimate here — this IS operational state. */}
            <span
              aria-hidden
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-healthy-soft text-healthy"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            <div>
              <div className="text-sm font-medium text-ink">All clear</div>
              <div className="text-sm text-ink-muted">No items need attention in this scope.</div>
            </div>
          </div>
        ) : (
          <ul className="md-stagger flex flex-col gap-2">
            {items.slice(0, WORKLIST_PREVIEW).map((alert) => (
              <WorklistRow key={alert.id} alert={alert} href={deviceHref} />
            ))}
          </ul>
        )}
      </section>

      {/* ---- By device type ---- */}
      {ov && ov.devices.by_kind.length > 0 && (
        <section aria-label="Devices by type">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">By type</h2>
          <div className="md-stagger grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
            {ov.devices.by_kind.map((k) => (
              <DeviceTypeCard key={k.kind} kind={k} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/** The "Live · updated Ns ago" pulse. Ticks a local clock so the label counts up between polls. */
function LivePulse({ updatedAt, fetching }: { updatedAt: number; fetching: boolean }) {
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const secs = updatedAt ? Math.max(0, Math.round((Date.now() - updatedAt) / 1000)) : null;
  const label = secs == null ? "connecting…" : secs < 2 ? "just now" : `${secs}s ago`;
  return (
    <div className="md-glow flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1.5 text-xs">
      <span className={`relative flex h-2 w-2 ${fetching ? "" : ""}`}>
        <span className={`absolute inline-flex h-full w-full rounded-full bg-healthy ${fetching ? "animate-ping opacity-75" : "opacity-0"}`} />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-healthy" />
      </span>
      <span className="text-ink-muted">Live · updated <span className="tabular-nums text-ink">{label}</span></span>
    </div>
  );
}

/** Live throughput + clients chart. Overlaid rx/tx area on a bps axis, clients as a line on
 *  its own scale, with a hover guide + tooltip. Renders a "collecting" state until 2+ points. */
function ActivityChart({ overview, loading }: { overview: DashboardOverview | undefined; loading: boolean }) {
  const series = overview?.activity.series ?? [];
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const W = 900;
  const H = 220;
  const padL = 8;
  const padR = 8;
  const padT = 14;
  const padB = 22;

  const nowPoint = overview?.activity.now;

  if (loading && series.length === 0) {
    // A chart-shaped skeleton rather than a line of text: a row of shimmering bars at seeded
    // heights sitting on a baseline, so the placeholder occupies the same shape the live graph
    // will. Heights are deterministic (no layout jitter on re-render) and purely decorative.
    const bars = [38, 62, 50, 74, 46, 68, 58, 82, 54, 70, 44, 64, 52, 78, 48, 66, 56, 72];
    return (
      <div className="md-card h-[220px] p-4" aria-busy aria-live="polite">
        <span className="sr-only">Loading activity…</span>
        <div className="flex h-full items-end gap-1.5">
          {bars.map((h, i) => (
            <div
              key={i}
              className="md-skeleton flex-1 rounded-sm"
              style={{ height: `${h}%`, ["--sk-delay" as never]: `${i * 50}ms` }}
            />
          ))}
        </div>
      </div>
    );
  }

  const enough = series.length >= 2;
  const maxBps = Math.max(1, ...series.map((p) => Math.max(p.rx_bps, p.tx_bps)));
  const maxCli = Math.max(1, ...series.map((p) => p.clients));
  const n = series.length;
  const x = (i: number) => padL + (n <= 1 ? 0 : (i / (n - 1)) * (W - padL - padR));
  const yB = (v: number) => padT + (1 - v / maxBps) * (H - padT - padB);
  const yC = (v: number) => padT + (1 - v / maxCli) * (H - padT - padB);

  const areaPath = (key: "rx_bps" | "tx_bps") => {
    if (!enough) return "";
    const top = series.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${yB(p[key]).toFixed(1)}`).join(" ");
    return `${top} L${x(n - 1).toFixed(1)},${(H - padB).toFixed(1)} L${x(0).toFixed(1)},${(H - padB).toFixed(1)} Z`;
  };
  const linePath = (key: "rx_bps" | "tx_bps" | "clients", y: (v: number) => number) =>
    enough ? series.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`).join(" ") : "";

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg || n === 0) return;
    const rect = svg.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const i = Math.round(((px - padL) / Math.max(1, W - padL - padR)) * (n - 1));
    setHover(Math.min(n - 1, Math.max(0, i)));
  };

  const hp = hover != null ? series[hover] : null;

  return (
    <div className="md-card p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Activity</span>
          <span className="flex items-center gap-1.5 text-xs text-ink-muted"><i className="inline-block h-2 w-2 rounded-sm" style={{ background: "rgb(var(--md-brand))" }} /> ↓ {fmtBps(nowPoint?.rx_bps ?? 0)}</span>
          <span className="flex items-center gap-1.5 text-xs text-ink-muted"><i className="inline-block h-2 w-2 rounded-sm" style={{ background: "rgb(var(--md-healthy))" }} /> ↑ {fmtBps(nowPoint?.tx_bps ?? 0)}</span>
          <span className="flex items-center gap-1.5 text-xs text-ink-muted"><i className="inline-block h-2 w-2 rounded-sm" style={{ background: "rgb(var(--md-ink-subtle))" }} /> {nowPoint?.clients ?? 0} clients</span>
        </div>
        <span className="text-[0.6875rem] text-ink-subtle">
          last {fmtWindow(overview?.activity.window_seconds ?? 3600)}
        </span>
      </div>

      {!enough ? (
        <div className="grid h-[200px] place-items-center rounded-lg border border-dashed border-line text-center text-sm text-ink-muted">
          <div className="flex flex-col items-center">
            {/* A quiet "sampling" pulse: a neutral ink dot with an expanding ring, so the empty
                state reads as working rather than stalled. Neutral ink (not status, not brand) —
                nothing here is a health state or a control. */}
            <span aria-hidden className="relative mb-3 flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ink-subtle opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-ink-subtle" />
            </span>
            Collecting live samples…
            <div className="mt-1 text-xs text-ink-subtle">The graph fills in as the controller polls devices (~1&nbsp;min cadence).</div>
          </div>
        </div>
      ) : (
        <div className="relative">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${W} ${H}`}
            className="w-full"
            style={{ height: "auto", touchAction: "none" }}
            role="img"
            aria-label="Network throughput and client count over time"
            onPointerMove={onMove}
            onPointerLeave={() => setHover(null)}
          >
            <defs>
              <linearGradient id="rxg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgb(var(--md-brand))" stopOpacity="0.28" />
                <stop offset="100%" stopColor="rgb(var(--md-brand))" stopOpacity="0.02" />
              </linearGradient>
              <linearGradient id="txg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgb(var(--md-healthy))" stopOpacity="0.22" />
                <stop offset="100%" stopColor="rgb(var(--md-healthy))" stopOpacity="0.02" />
              </linearGradient>
            </defs>

            {/* baseline */}
            <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="rgb(var(--md-line))" strokeWidth="1" />

            {/* rx (download) + tx (upload) areas */}
            <path d={areaPath("rx_bps")} fill="url(#rxg)" />
            <path d={areaPath("tx_bps")} fill="url(#txg)" />
            <path d={linePath("rx_bps", yB)} fill="none" stroke="rgb(var(--md-brand))" strokeWidth="1.8" strokeLinejoin="round" />
            <path d={linePath("tx_bps", yB)} fill="none" stroke="rgb(var(--md-healthy))" strokeWidth="1.8" strokeLinejoin="round" />

            {/* clients (secondary scale) */}
            <path d={linePath("clients", yC)} fill="none" stroke="rgb(var(--md-ink-subtle))" strokeWidth="1.2" strokeDasharray="3 3" opacity="0.8" />

            {/* hover guide */}
            {hp && hover != null && (
              <g>
                <line x1={x(hover)} y1={padT} x2={x(hover)} y2={H - padB} stroke="rgb(var(--md-ink-subtle))" strokeWidth="1" opacity="0.5" />
                <circle cx={x(hover)} cy={yB(hp.rx_bps)} r="3.2" fill="rgb(var(--md-brand))" />
                <circle cx={x(hover)} cy={yB(hp.tx_bps)} r="3.2" fill="rgb(var(--md-healthy))" />
              </g>
            )}
          </svg>

          {hp && hover != null && (
            <div
              className="pointer-events-none absolute top-1 z-10 rounded-lg border border-line bg-overlay px-3 py-2 text-xs shadow-sheet"
              style={{
                left: `${(x(hover) / W) * 100}%`,
                transform: hover > n / 2 ? "translateX(-105%)" : "translateX(5%)",
              }}
            >
              <div className="mb-1 font-mono text-[0.6875rem] text-ink-subtle">
                {/* parseApiTime: the API emits naive-UTC, which bare new Date() reads as LOCAL. */}
                {new Date(parseApiTime(hp.t) ?? hp.t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="flex items-center gap-1.5" style={{ color: "rgb(var(--md-brand))" }}>↓ {fmtBps(hp.rx_bps)}</div>
              <div className="flex items-center gap-1.5" style={{ color: "rgb(var(--md-healthy))" }}>↑ {fmtBps(hp.tx_bps)}</div>
              <div className="text-ink-muted">{hp.clients} clients</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** A compact SVG ring gauge for the fleet-health percentage. */
function HealthGauge({ value, tone }: { value: number | null; tone: "healthy" | "warn" | "critical" | "unknown" }) {
  const r = 22;
  const c = 2 * Math.PI * r;
  const pct = value ?? 0;
  const stroke =
    tone === "healthy" ? "var(--gauge-healthy)" : tone === "warn" ? "var(--gauge-warn)" : tone === "critical" ? "var(--gauge-critical)" : "var(--gauge-unknown)";
  return (
    <svg width="60" height="60" viewBox="0 0 60 60" role="img" aria-label={value == null ? "Fleet health unavailable" : `Fleet health ${value} percent`}
      style={{
        ["--gauge-healthy" as never]: "rgb(var(--md-healthy))",
        ["--gauge-warn" as never]: "rgb(var(--md-warn))",
        ["--gauge-critical" as never]: "rgb(var(--md-critical))",
        ["--gauge-unknown" as never]: "rgb(var(--md-ink-subtle))",
      }}>
      <circle cx="30" cy="30" r={r} fill="none" stroke="rgb(var(--md-line))" strokeWidth="6" />
      <circle
        cx="30" cy="30" r={r} fill="none" stroke={stroke} strokeWidth="6" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} transform="rotate(-90 30 30)"
      />
      <text x="30" y="34" textAnchor="middle" className="fill-ink" fontSize="15" fontWeight="600">
        {value == null ? "—" : value}
      </text>
    </svg>
  );
}

/** One device-kind card: type, online/total, and an offline count when any are down. */
function DeviceTypeCard({
  kind,
}: {
  kind: { kind: string; label: string; online: number; offline: number; total: number };
}) {
  return (
    <div className="md-card p-4">
      <div className="text-xs text-ink-muted">{kind.label}</div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold tabular-nums text-ink">{kind.total}</span>
        <span className="text-xs text-ink-subtle">total</span>
      </div>
      <div className="mt-1 flex items-center gap-3 text-xs">
        <span className="text-healthy">{kind.online} online</span>
        {kind.offline > 0 && <span className="text-warn">{kind.offline} offline</span>}
      </div>
    </div>
  );
}

function WorklistRow({ alert, href }: { alert: HealthAlert; href: (serial: string) => string }) {
  return (
    <li className="md-card flex items-start gap-3 p-4">
      <StatusPill status={alert.severity} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-ink">{alertGuide(alert.kind, alert.device_kind).label}</span>
          <KindChip kind={alert.kind} />
        </div>
        <div className="mt-0.5 truncate text-sm text-ink-muted">{alert.title}</div>
        <div className="mt-0.5 text-xs text-ink-muted">
          {alert.device_name && <span className="text-ink">{alert.device_name} · </span>}
          {alert.target_type === "device" ? <span className="font-mono">{alert.target_id}</span> : alert.target_id}
          {alert.site_name ? ` · ${alert.site_name}` : ""}
          {alert.created_at ? ` · since ${relTime(alert.created_at).replace(/ ago$/, "")}` : ""}
        </div>
        {alert.detail && <div className="mt-0.5 text-sm text-ink-muted">{alert.detail}</div>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {alert.proposal_id && (
          <Button variant="ghost" disabled title="AI explanation — Phase 3">
            Explain
          </Button>
        )}
        {alert.target_type === "device" && (
          <Link to={href(alert.target_id)}>
            <Button variant="secondary">View</Button>
          </Link>
        )}
      </div>
    </li>
  );
}

/** Small chip for the alert kind, e.g. "ai_anomaly" -> "AI ANOMALY". */
function KindChip({ kind }: { kind: string }) {
  const label = kind.replace(/_/g, " ").replace(/\bai\b/gi, "AI");
  return (
    <span className="shrink-0 rounded border border-line px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-ink-subtle">
      {label}
    </span>
  );
}
